import { Button, Checkbox, Modal, Paper } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useMemo, useRef } from 'react';
import { useBooleanState } from 'react-use-object-state';
import { JupiterTheme } from '../../theme/types';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    paper: {
      outline: 'none',
      position: 'absolute',
      width: theme.spacing(60),
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[5],
      padding: theme.spacing(4),
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
    checkbox: {
      marginRight: theme.spacing(2),
    },
    checkboxAndMessageContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    buttons: {
      marginTop: theme.spacing(2),
      display: 'flex',
      justifyContent: 'flex-end',
      '& button:focus': {
        textDecoration: 'none',
      },
      '& button + button': {
        marginLeft: theme.spacing(1),
      },
    },
    default: {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      color: theme.palette.background.paper,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        borderColor: theme.palette.primary.dark,
      },
    },
    delete: {
      backgroundColor: theme.palette.red[500],
      borderColor: theme.palette.red[500],
      color: theme.palette.background.paper,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.red[700],
        borderColor: theme.palette.red[700],
      },
    },
  })
);

export interface Props {
  open: boolean;
  message: any;
  onConfirm: () => void;
  onClose: () => void;
  onDeny?: () => void;
  variant?: 'default' | 'delete';
  confirmButtonText?: string;
  denyButtonText?: string;
  autoFocus?: boolean;
  withCheckbox?: boolean;
}

const ConfirmationPrompt = ({
  open,
  variant = 'default',
  onClose,
  onConfirm,
  message,
  confirmButtonText = 'Continue',
  denyButtonText = 'Cancel',
  onDeny,
  autoFocus = false,
  withCheckbox = false,
}: Props) => {
  const classes = useStyles();
  const checkboxChecked = useBooleanState(false);
  const confirmationButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const buttonText: string = useMemo(() => {
    if (variant === 'delete') return 'delete';
    return confirmButtonText;
  }, [variant, confirmButtonText]);

  const buttonClassname = useMemo(() => {
    if (variant === 'delete') return classes.delete;
    return classes.default;
  }, [variant]);

  return (
    <Modal open={open} onClose={onClose} data-testid="ConfirmationPrompt">
      <Paper className={classes.paper}>
        {withCheckbox ? (
          <div className={classes.checkboxAndMessageContainer}>
            <Checkbox
              data-testid="confirmation-prompt-checkbox"
              value={checkboxChecked.state}
              onClick={checkboxChecked.toggle}
              className={classes.checkbox}
            />
            <div>{message}</div>
          </div>
        ) : (
          <div>{message}</div>
        )}
        <div className={classes.buttons}>
          <Button
            onClick={
              onDeny
                ? (event) => {
                    event.stopPropagation();
                    onDeny();
                  }
                : onClose
            }
            data-testid="confirmation-deny-button"
          >
            {denyButtonText}
          </Button>
          <Button
            disabled={withCheckbox && !checkboxChecked.state}
            variant="contained"
            color="inherit"
            onClick={(event) => {
              event.stopPropagation();
              handleConfirm();
            }}
            className={buttonClassname}
            data-testid="confirmation-confirm-button"
            ref={(element) => {
              confirmationButtonRef.current = element;
              if (autoFocus && open && confirmationButtonRef.current) {
                confirmationButtonRef.current.focus();
              }
            }}
          >
            {buttonText}
          </Button>
        </div>
      </Paper>
    </Modal>
  );
};

export default ConfirmationPrompt;
